<template>
    <b-modal :visible="value" @change="onVisibleChange" :title="isCreate ? 'Create AR Account' : 'Manage AR Account'"
        size="lg" hide-footer>
        <b-form @submit.prevent="save">
            <SearchComponent v-if="isCreate" v-model="responsiblePatient" label="Responsible Patient"
                search-u-r-l="/Patient" required />
            <b-form-group v-else label="Responsible Patient">
                <div class="form-control-plaintext">{{ responsiblePatientName }}</div>
            </b-form-group>

            <b-form-group label="Default Credit Limit" v-if="isCreate">
                <b-form-input v-model.number="form.defaultCreditLimit" type="number" min="0" step="0.01" />
            </b-form-group>

            <b-form-group label="Statement Delivery Methods">
                <b-form-checkbox-group v-model="form.deliveryMethods" :options="deliveryOptions" stacked />
            </b-form-group>

            <b-form-group label="Notes">
                <b-form-textarea v-model="form.notes" rows="2" />
            </b-form-group>

            <b-form-group label="Linked Patients">
                <div class="d-flex align-items-start" style="gap: 6px;">
                    <div class="flex-grow-1">
                        <SearchComponent v-model="linkInput" :hide-label="true" search-u-r-l="/Patient" clearable />
                    </div>
                    <b-button size="sm" variant="outline-primary" :disabled="!linkInput?.id" @click="addLink">
                        Add
                    </b-button>
                </div>
                <div class="mt-1">
                    <b-badge v-for="linked in form.linkedPatients" :key="linked.id" variant="info" class="mr-1">
                        {{ patientDisplayName(linked) }}
                        <span style="cursor: pointer;" @click="removeLink(linked.id)">&times;</span>
                    </b-badge>
                    <span v-if="!form.linkedPatients.length" class="text-muted">None</span>
                </div>
            </b-form-group>

            <div class="text-right">
                <b-button variant="secondary" class="mr-2" @click="close">Cancel</b-button>
                <b-button variant="primary" type="submit" :disabled="saving || !canSave">Save</b-button>
            </div>
        </b-form>

        <template v-if="!isCreate">
            <hr />
            <h6>Account Actions</h6>
            <div class="d-flex flex-wrap" style="gap: 6px;">
                <b-button v-if="canClose" size="sm" variant="warning" :disabled="saving" @click="setStatus(2)">
                    Suspend
                </b-button>
                <b-button v-if="canClose" size="sm" variant="secondary" :disabled="saving" @click="setStatus(3)">
                    Close
                </b-button>
                <b-button v-if="canClose" size="sm" variant="danger" :disabled="saving" @click="setStatus(4)">
                    Write Off Account
                </b-button>
                <b-button v-if="canClose" size="sm" variant="success" :disabled="saving" @click="setStatus(1)">
                    Reactivate
                </b-button>
            </div>
            <small v-if="canClose" class="text-muted d-block mt-1">
                Writing off the account marks the whole account uncollectible and stops new charges.
                To credit a single amount against the balance, use <strong>Write Off Balance</strong>
                on the AR card.
            </small>

            <div v-if="canOverrideLimit" class="mt-3">
                <label class="mb-1">Credit Limit Override</label>
                <div class="d-flex" style="gap: 6px;">
                    <b-form-input v-model.number="creditLimitInput" type="number" min="0" step="0.01" />
                    <b-button size="sm" variant="outline-primary" :disabled="saving" @click="saveCreditLimit">
                        Set
                    </b-button>
                </div>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Patient } from "@/models/Patient";
import {
    ARAccountSummary,
    ARDeliveryMethodOptions,
    arDeliveryArrayToFlags,
    arDeliveryFlagsToArray,
} from "@/models/AR";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import SearchComponent from "@/components/SearchComponent.vue";

interface ManageForm {
    responsiblePatientId: number | null;
    defaultCreditLimit: number | null;
    deliveryMethods: number[];
    notes: string;
    linkedPatients: Patient[];
}

@Component({
    name: "ARAccountManageModal",
    components: { SearchComponent },
})
export default class ARAccountManageModal extends Vue {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop() patient!: Patient;
    @Prop({ default: null }) accountId!: number | null;

    private saving = false;
    // Patient instances rather than raw ids so SearchComponent renders the standard
    // patient label instead of falling back to printing the id.
    private responsiblePatient: Patient = new Patient();
    // Nullable because the linked-patient search is clearable, and v-select emits null on clear.
    private linkInput: Patient | null = new Patient();
    private creditLimitInput: number | null = null;
    private deliveryOptions = ARDeliveryMethodOptions;

    private form: ManageForm = this.emptyForm();

    get isCreate(): boolean {
        return !this.accountId;
    }

    get canClose(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Account/Close");
    }

    get canOverrideLimit(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Credit Limit/Override Account Limit");
    }

    get canSave(): boolean {
        return !!this.responsiblePatientId;
    }

    // The responsible patient is chosen by search when creating and is fixed once the account
    // exists — reassigning it would move the balance on an account that already has transactions.
    get responsiblePatientId(): number | null {
        return (this.isCreate ? this.responsiblePatient?.id : this.form.responsiblePatientId) ?? null;
    }

    get responsiblePatientName(): string {
        return this.responsiblePatient?.id ? this.patientDisplayName(this.responsiblePatient) : "";
    }

    @Watch("value")
    onShow(visible: boolean): void {
        if (!visible) return;
        this.linkInput = new Patient();
        if (this.isCreate) {
            this.form = this.emptyForm();
            this.responsiblePatient = new Patient(this.patient);
        } else {
            this.responsiblePatient = new Patient();
            this.loadAccount();
        }
    }

    private emptyForm(): ManageForm {
        return {
            responsiblePatientId: null,
            defaultCreditLimit: null,
            deliveryMethods: [],
            notes: "",
            linkedPatients: [],
        };
    }

    private async loadAccount(): Promise<void> {
        if (!this.accountId) return;
        try {
            const response = await axios.get<ARAccountSummary>(`/AR/accounts/${this.accountId}`);
            const acct = response.data;
            const linkedIds = (acct.linkedPatients ?? [])
                .filter(lp => lp.active !== false && lp.patientId != null)
                .map(lp => lp.patientId as number);

            // Resolve names before populating the form so the modal never paints an
            // id placeholder that a moment later turns into a name.
            const [responsible, linked] = await Promise.all([
                acct.responsiblePatientId ? this.resolvePatient(acct.responsiblePatientId) : Promise.resolve(null),
                Promise.all(linkedIds.map(id => this.resolvePatient(id))),
            ]);

            this.form = {
                responsiblePatientId: acct.responsiblePatientId ?? null,
                defaultCreditLimit: acct.defaultCreditLimit ?? null,
                deliveryMethods: arDeliveryFlagsToArray(acct.statementDeliveryMethods),
                notes: acct.notes ?? "",
                linkedPatients: linked,
            };
            this.creditLimitInput = acct.defaultCreditLimit ?? null;
            if (responsible) this.responsiblePatient = responsible;
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    // The account only stores patient ids, so names come from a lookup. An unresolved patient
    // still yields a Patient carrying the id: dropping it would silently unlink that patient on
    // the next save. A patient that no longer exists comes back as 204 No Content rather than an
    // error, so an id-less body counts as unresolved too.
    private async resolvePatient(patientId: number): Promise<Patient> {
        if (this.patient?.id === patientId) return new Patient(this.patient);
        try {
            const response = await axios.get<Patient>(`/Patient/${patientId}`);
            if (response.data?.id) return new Patient(response.data);
        } catch {
            // Fall through to the id-only placeholder below.
        }
        const unresolved = new Patient();
        unresolved.id = patientId;
        return unresolved;
    }

    patientDisplayName(patient: Patient): string {
        return patient?.lastName ? patient.displayNamesForPerson() : `Patient #${patient?.id}`;
    }

    addLink(): void {
        const selected = this.linkInput;
        if (selected?.id && !this.form.linkedPatients.some(p => p.id === selected.id)) {
            this.form.linkedPatients.push(selected);
        }
        this.linkInput = new Patient();
    }

    removeLink(patientId: number): void {
        this.form.linkedPatients = this.form.linkedPatients.filter(p => p.id !== patientId);
    }

    private buildPayload() {
        return {
            responsiblePatientId: this.responsiblePatientId,
            defaultCreditLimit: this.form.defaultCreditLimit,
            statementDeliveryMethods: this.form.deliveryMethods.length
                ? arDeliveryArrayToFlags(this.form.deliveryMethods)
                : null,
            notes: this.form.notes,
            linkedPatients: this.form.linkedPatients.map(p => ({ patientId: p.id })),
        };
    }

    async save(): Promise<void> {
        if (!this.canSave) return;
        try {
            this.saving = true;
            if (this.isCreate) {
                await axios.post("/AR/accounts", this.buildPayload());
            } else {
                await axios.put(`/AR/accounts/${this.accountId}`, this.buildPayload());
            }
            this.$notification(NotificationOptions.success("AR account saved."));
            this.$emit("saved");
            this.close();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    async setStatus(status: number): Promise<void> {
        if (!this.accountId) return;
        try {
            this.saving = true;
            await axios.post(`/AR/accounts/${this.accountId}/status`, { status });
            this.$notification(NotificationOptions.success("Account status updated."));
            this.$emit("saved");
            this.close();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    async saveCreditLimit(): Promise<void> {
        if (!this.accountId) return;
        try {
            this.saving = true;
            await axios.post(`/AR/accounts/${this.accountId}/credit-limit`, { creditLimit: this.creditLimitInput });
            this.$notification(NotificationOptions.success("Credit limit updated."));
            this.$emit("saved");
            this.close();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }

    close(): void {
        this.$emit("input", false);
    }
}
</script>

<style scoped></style>
